// src/pages/ManagedTitlesPage.jsx
// Publisher Managed Titles List Page - displays all publisher titles with covers
// PHASE 2: Primary TTS monitoring surface with resilient polling
// PHASE 3: User-facing TTS recovery with confirmation modal
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PublisherNav from '../components/PublisherNav';
import { BookOpen, Plus, Book, Archive, CheckCircle, ChevronRight, Clock, AlertCircle, Search, SlidersHorizontal, Loader2, WifiOff, RefreshCw, Brain, RotateCcw, Wrench, Copy, Check } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { Progress } from '@/components/ui/progress';
import { BACKEND_URL as backendUrl } from '@/config/api';
// REMOVED: TTSRecoveryModal - P0-3: No UI recovery controls
import { toast } from 'sonner';

const ManagedTitlesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusManuscriptId = searchParams.get('focus');
  const { user } = useAuth();
  const [titles, setTitles] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [authorFilter, setAuthorFilter] = useState('all');
  
  // P0 FINAL HOLE: TTS polling REMOVED - all TTS state comes from initial page load only
  // REMOVED: ttsStatuses, networkError, pollingRef, retryCountRef
  
  // REMOVED: TTS Recovery modal state - P0-3: No UI recovery controls
  
  // REMOVED: Hard Reset state - P0 UX Directive: No user-operated recovery
  const [copiedId, setCopiedId] = useState(null);
  
  // backendUrl imported from centralized config as BACKEND_URL
  
  // Copy manuscript ID to clipboard
  const copyManuscriptId = (e, manuscriptId) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(manuscriptId);
    setCopiedId(manuscriptId);
    toast.success('Manuscript ID copied!');
    setTimeout(() => setCopiedId(null), 2000);
  };
  
  // REMOVED: handleHardReset function - P0 UX Directive
  // Recovery is now backend-owned and automatic

  // P0 FINAL HOLE: TTS polling REMOVED - Status comes from page refresh only
  // fetchTTSStatus DELETED - No polling allowed
  // pollAllProcessingTitles DELETED - No polling allowed

  // Auto-focus on the manuscript from URL param
  useEffect(() => {
    if (focusManuscriptId && !loading) {
      // Scroll to the focused manuscript card if it exists
      const element = document.getElementById(`title-${focusManuscriptId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-indigo-500');
        setTimeout(() => {
          element.classList.remove('ring-2', 'ring-indigo-500');
        }, 3000);
      }
    }
  }, [focusManuscriptId, loading]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // Fetch both titles and authors in parallel
      const [titlesRes, authorsRes] = await Promise.all([
        fetch(`${backendUrl}/api/publisher/titles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${backendUrl}/api/publisher/authors`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (titlesRes.ok) {
        const data = await titlesRes.json();
        const loadedTitles = data.titles || [];
        setTitles(loadedTitles);
        
        // CRITICAL: Immediately fetch TTS status for any title that might be processing/stalled
        // This ensures we detect stalled jobs even after page reload
        const potentiallyProcessing = loadedTitles.filter(t => 
          t.audio_status === 'processing' || 
          t.audio_status === 'generating' ||
          t.audio_status === 'stalled'
        );
        
        if (potentiallyProcessing.length > 0) {
          // P0 FINAL HOLE: No polling - status comes from initial data load only
          console.log(`[TTS Init] ${potentiallyProcessing.length} titles in processing state`);
        }
      }
      
      if (authorsRes.ok) {
        const data = await authorsRes.json();
        setAuthors(data.authors || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get all author names for filter dropdown (from managed authors list)
  const authorOptions = useMemo(() => {
    const authorNames = authors
      .map(a => a.pen_name || a.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    return authorNames;
  }, [authors]);

  // Filter and sort titles
  const filteredTitles = useMemo(() => {
    let result = [...titles];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(title => 
        (title.title || title.title_name || '').toLowerCase().includes(query) ||
        (title.subtitle || '').toLowerCase().includes(query) ||
        (title.author_display_name || '').toLowerCase().includes(query)
      );
    }
    
    // Author filter
    if (authorFilter !== 'all') {
      result = result.filter(title => title.author_display_name === authorFilter);
    }
    
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'title-az':
          return (a.title || a.title_name || '').localeCompare(b.title || b.title_name || '');
        case 'title-za':
          return (b.title || b.title_name || '').localeCompare(a.title || a.title_name || '');
        case 'author':
          return (a.author_display_name || '').localeCompare(b.author_display_name || '');
        default:
          return 0;
      }
    });
    
    return result;
  }, [titles, searchQuery, sortBy, authorFilter]);

  // Get cover URL
  const getCoverUrl = (title) => {
    if (title.cover_image_url) {
      return `${backendUrl}${title.cover_image_url}?t=${Date.now()}`;
    }
    return null;
  };

  // Get audio status display - uses title.audio_status from initial load only
  // P0 FINAL HOLE: No polling - refresh page to see updated status
  const getAudioStatus = (title) => {
    const status = title.audio_status;
    
    if (status === 'completed') {
      return { icon: CheckCircle, text: 'Audio Ready', color: 'text-emerald-400', isProcessing: false };
    } else if (status === 'running' || status === 'processing' || status === 'generating' || status === 'queued') {
      return { 
        icon: Loader2, 
        text: status === 'queued' ? 'Queued' : 'Processing', 
        color: 'text-amber-400', 
        isProcessing: true
      };
    } else if (status === 'stalled') {
      // STALLED: Per spec, show "Stalled" - backend will auto-fail after timeout
      return { 
        icon: AlertCircle, 
        text: 'Stalled', 
        color: 'text-orange-400', 
        isProcessing: false,
        isStalled: true
      };
    } else if (status === 'failed') {
      return { 
        icon: AlertCircle, 
        text: 'Failed', 
        color: 'text-red-400', 
        isProcessing: false
      };
    }
    return { icon: Clock, text: 'Pending', color: 'text-slate-400', isProcessing: false };
  };

  // REMOVED: openRecoveryModal - P0-3: No UI recovery controls
  // REMOVED: handleRecoverySuccess - P0-3: No UI recovery controls

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      <main className="mx-auto max-w-6xl px-4 py-8">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="rounded-full bg-indigo-600/20 p-2">
                <BookOpen className="w-6 h-6 text-indigo-400" />
              </div>
              <h1 className="text-2xl font-bold">Managed Titles</h1>
            </div>
            <p className="text-slate-400">
              View and manage all titles in your publishing catalog.
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/publisher')}
              className="inline-flex items-center gap-2 rounded-full bg-slate-700 hover:bg-slate-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors"
            >
              Publisher Home
            </button>
            <button
              onClick={() => navigate('/publisher/authors')}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 hover:bg-indigo-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Title
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-indigo-500 border-r-transparent border-b-blue-500 border-l-transparent"></div>
            <p className="mt-4 text-slate-400">Loading titles...</p>
          </div>
        ) : titles.length === 0 ? (
          /* Empty State */
          <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-12 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-indigo-600/20 flex items-center justify-center mb-6">
              <BookOpen className="w-8 h-8 text-indigo-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-200 mb-2">No Titles Yet</h2>
            <p className="text-slate-400 max-w-md mx-auto mb-6">
              Titles are managed under Authors. Create an author first, then upload titles through their Author Console.
            </p>
            <button
              onClick={() => navigate('/publisher/authors')}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 hover:bg-indigo-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors"
            >
              <Plus className="w-4 h-4" />
              Go to Authors
            </button>
          </div>
        ) : (
          <>
            {/* Search and Filter Controls */}
            <div className="mb-6 rounded-xl border border-slate-700 bg-slate-900/50 p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search titles, authors..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg bg-slate-800 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                
                {/* Sort Dropdown */}
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-slate-500" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-slate-100 focus:outline-none focus:border-indigo-500 text-sm"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="title-az">Title (A-Z)</option>
                    <option value="title-za">Title (Z-A)</option>
                    <option value="author">Author</option>
                  </select>
                </div>
                
                {/* Author Filter */}
                <select
                  value={authorFilter}
                  onChange={(e) => setAuthorFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-slate-100 focus:outline-none focus:border-indigo-500 text-sm min-w-[160px]"
                >
                  <option value="all">All Authors</option>
                  {authorOptions.map(author => (
                    <option key={author} value={author}>{author}</option>
                  ))}
                </select>
              </div>
              
              {/* Results count */}
              <div className="mt-3 text-xs text-slate-500">
                Showing {filteredTitles.length} of {titles.length} titles
              </div>
            </div>

            {/* Titles Grid - Smaller covers */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filteredTitles.map((title) => {
                const coverUrl = getCoverUrl(title);
                const isArchived = title.status === 'archived';
                const audioStatus = getAudioStatus(title);
                const StatusIcon = audioStatus.icon;
                const progressPercent = audioStatus.total > 0 
                  ? Math.round((audioStatus.completed / audioStatus.total) * 100) 
                  : 0;
                
                return (
                  <Link
                    key={title.id}
                    id={`title-${title.manuscript_id}`}
                    to={`/publisher/titles/${title.id}/edit`}
                    className={`rounded-xl border bg-slate-900/50 overflow-hidden transition-all ${
                      isArchived 
                        ? 'border-slate-700/50 opacity-60' 
                        : audioStatus.isProcessing
                          ? 'border-amber-500/50 shadow-lg shadow-amber-500/10'
                          : 'border-slate-700 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10'
                    }`}
                  >
                    {/* Cover Image - Reduced size */}
                    <div className="aspect-[3/4] w-full bg-slate-800 relative">
                      {coverUrl ? (
                        <img
                          src={coverUrl}
                          alt={title.title || title.title_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Book className="w-8 h-8 text-slate-600" />
                        </div>
                      )}
                      
                      {/* Status Badge Overlay - Binary: Ready, Preparing, or Failed */}
                      {(isArchived || audioStatus.text !== 'Audio Ready') && (
                        <div className="absolute top-2 right-2">
                          {isArchived ? (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-slate-900/80 text-[10px] text-slate-400">
                              <Archive className="w-2.5 h-2.5" />
                              Archived
                            </span>
                          ) : audioStatus.text === 'Failed' ? (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-900/80 text-[10px] text-red-300">
                              <AlertCircle className="w-2.5 h-2.5" />
                              Failed
                            </span>
                          ) : audioStatus.isProcessing || audioStatus.isStalled ? (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-slate-900/80 text-[10px] text-slate-300">
                              <Loader2 className="w-2.5 h-2.5 animate-spin" />
                              Preparing
                            </span>
                          ) : null}
                        </div>
                      )}
                      
                      {/* FAIL Overlay - Binary contract */}
                      {audioStatus.text === 'Failed' && (
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-red-900/95 to-transparent p-3">
                          <div className="text-center">
                            <AlertCircle className="w-6 h-6 text-red-400 mx-auto mb-1" />
                            <p className="text-xs text-red-300 font-semibold">Processing Failed</p>
                            <p className="text-[10px] text-red-400/70 mt-1">Support notified</p>
                          </div>
                        </div>
                      )}
                      
                      {/* PREPARING Overlay - Minimal, no internals exposed */}
                      {(audioStatus.isProcessing || audioStatus.isStalled) && audioStatus.text !== 'Failed' && (
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/95 to-transparent p-2">
                          <div className="text-center">
                            <Loader2 className="w-5 h-5 text-slate-400 mx-auto mb-1 animate-spin" />
                            <p className="text-[10px] text-slate-400">Preparing</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Title Info - Compact */}
                    <div className="p-2">
                      <h3 className="font-medium text-sm text-slate-100 line-clamp-2 leading-tight">
                        {title.title || title.title_name}
                      </h3>
                      
                      {/* Author */}
                      <p className="text-[10px] text-slate-500 mt-1 line-clamp-1">
                        {title.author_display_name || 'Unknown Author'}
                      </p>
                      
                      {/* Manuscript ID - Debug Info (temporary) */}
                      {title.manuscript_id && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <button
                            onClick={(e) => copyManuscriptId(e, title.manuscript_id)}
                            className="flex items-center gap-1 text-[9px] text-slate-600 hover:text-slate-400 font-mono bg-slate-800/50 px-1.5 py-0.5 rounded transition-colors"
                            title="Click to copy manuscript ID"
                          >
                            {copiedId === title.manuscript_id ? (
                              <Check className="w-2.5 h-2.5 text-emerald-400" />
                            ) : (
                              <Copy className="w-2.5 h-2.5" />
                            )}
                            <span className="truncate max-w-[100px]">{title.manuscript_id.slice(0, 12)}...</span>
                          </button>
                        </div>
                      )}
                      
                      {/* REMOVED: Hard Reset Button - P0 UX Directive: No user-operated recovery */}
                      {/* Recovery is now backend-owned and automatic */}
                      
                      {/* EI Status Indicator */}
                      {title.ei_status && (
                        <div className={`flex items-center gap-1 mt-1 text-[9px] ${
                          title.ei_status === 'completed' ? 'text-purple-400' :
                          title.ei_status === 'running' ? 'text-blue-400' :
                          title.ei_status === 'failed' ? 'text-red-400' :
                          'text-slate-500'
                        }`}>
                          <Brain className={`w-2.5 h-2.5 ${title.ei_status === 'running' ? 'animate-pulse' : ''}`} />
                          <span>
                            {title.ei_status === 'completed' ? 'EI Ready' :
                             title.ei_status === 'running' ? `EI ${title.ei_completed}/${title.ei_total}` :
                             title.ei_status === 'failed' ? 'EI Failed' :
                             title.ei_status === 'queued' ? 'EI Queued' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
            
            {/* No results */}
            {filteredTitles.length === 0 && titles.length > 0 && (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No titles match your search criteria</p>
                <button
                  onClick={() => { setSearchQuery(''); setAuthorFilter('all'); }}
                  className="mt-3 text-sm text-indigo-400 hover:text-indigo-300"
                >
                  Clear filters
                </button>
              </div>
            )}
          </>
        )}
      </main>
      <UniversalFooter />
      
      {/* REMOVED: TTSRecoveryModal - P0-3: No UI recovery controls */}
    </div>
  );
};

export default ManagedTitlesPage;
